/**
 * Backoffice role constants and helpers.
 *
 * Backend roles (from hotel-touil-api/src/models/role.ts):
 *   ROLE_USER          = 1
 *   ROLE_ADMIN         = 2
 *   ROLE_HOTEL_MANAGER = 3
 *   ROLE_MARKETPLACE   = 4
 *   ROLE_SUPER_ADMIN   = 5
 *   ROLE_RECEPTIONIST  = 6  → "Réservation" display role
 *   ROLE_EDITOR_HOTELS = 7  ┐
 *   ROLE_EDITOR_TOURS  = 8  ├ "Éditeur" display role (any of these three)
 *   ROLE_EDITOR_PAGES  = 9  ┘
 *
 * Backoffice display roles:
 *   Super Admin  → ROLE_SUPER_ADMIN          : tout voir, tout gérer, créer tous les utilisateurs
 *   Admin        → ROLE_ADMIN                : gestion équipe (créer Éditeur & Réservation), accès complet sauf config globale
 *   Éditeur      → ROLE_EDITOR_HOTELS/TOURS/PAGES : créer/modifier circuits, hôtels, pages
 *   Réservation  → ROLE_RECEPTIONIST         : suivi complet des réservations tours & hôtels
 */

export const ROLES = {
  SUPER_ADMIN: 'ROLE_SUPER_ADMIN',
  ADMIN: 'ROLE_ADMIN',
  RECEPTIONIST: 'ROLE_RECEPTIONIST',
  EDITOR_HOTELS: 'ROLE_EDITOR_HOTELS',
  EDITOR_TOURS: 'ROLE_EDITOR_TOURS',
  EDITOR_PAGES: 'ROLE_EDITOR_PAGES',
} as const

export type RoleName = (typeof ROLES)[keyof typeof ROLES]

/** All roles allowed to log in to the backoffice */
export const BACKOFFICE_ALLOWED_ROLES: string[] = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.RECEPTIONIST,
  ROLES.EDITOR_HOTELS,
  ROLES.EDITOR_TOURS,
  ROLES.EDITOR_PAGES,
]

/**
 * Editor sub-roles — any of these three means "Éditeur" in the backoffice.
 * When creating an Éditeur user, all three roles are assigned so the user
 * has access to hotels, tours, and pages content.
 */
export const EDITOR_ROLES: string[] = [
  ROLES.EDITOR_HOTELS,
  ROLES.EDITOR_TOURS,
  ROLES.EDITOR_PAGES,
]

// ─── Predicates ────────────────────────────────────────────────────────────────

export const isSuperAdmin = (role?: string | null): boolean =>
  role === ROLES.SUPER_ADMIN

/** True for both Admin and Super Admin (super admin can do everything admin can). */
export const isAdmin = (role?: string | null): boolean =>
  role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN

/** True for any editor sub-role. */
export const isEditor = (role?: string | null): boolean =>
  role != null && EDITOR_ROLES.includes(role)

/** True for the Réservation role. */
export const isReservation = (role?: string | null): boolean =>
  role === ROLES.RECEPTIONIST

// ─── Feature-level access ──────────────────────────────────────────────────────

export const canSeeReports = (role?: string | null): boolean =>
  isAdmin(role)

export const canSeeHotelsContent = (role?: string | null): boolean =>
  isAdmin(role) || isEditor(role)

export const canSeeHotelReservations = (role?: string | null): boolean =>
  isAdmin(role)

export const canSeeNewsletter = (role?: string | null): boolean =>
  isAdmin(role)

export const canSeeToursContent = (role?: string | null): boolean =>
  isAdmin(role) || isEditor(role)

export const canSeeTourReservations = (role?: string | null): boolean =>
  isAdmin(role) || isReservation(role)

/** Mail Orders (payment links): admin, super admin, and the Réservation role. */
export const canSeeMailOrders = (role?: string | null): boolean =>
  isAdmin(role) || isReservation(role)

export const canSeeUserManagement = (role?: string | null): boolean =>
  isAdmin(role)

export const canEditTeamUsers = (role?: string | null): boolean =>
  isAdmin(role)

export const canDeleteTeamUsers = (role?: string | null): boolean =>
  isSuperAdmin(role)

export const canToggleTeamUserStatus = (role?: string | null): boolean =>
  isAdmin(role)

export const canSeeClients = (role?: string | null): boolean =>
  isAdmin(role)

export const canSeeReviews = (role?: string | null): boolean =>
  isAdmin(role) || isEditor(role)

export const canSeeBlog = (role?: string | null): boolean =>
  isAdmin(role) || isEditor(role)

export const canSeePages = (role?: string | null): boolean =>
  isAdmin(role) || isEditor(role)

/** Global settings (header, footer, legal): super admin only. */
export const canSeeGlobalSettings = (role?: string | null): boolean =>
  isSuperAdmin(role)

/** Technical settings (facilities, themes, options): admin and above. */
export const canSeeContentSettings = (role?: string | null): boolean =>
  isAdmin(role)

/** Tour configurations: admin, super admin, and editors. */
export const canSeeTourConfigurations = (role?: string | null): boolean =>
  isAdmin(role) || isEditor(role)

/** Whether the Paramètres submenu should appear at all. */
export const canSeeSettings = (role?: string | null): boolean =>
  isAdmin(role) // super admin is already covered by isAdmin

// ─── Default route per role ────────────────────────────────────────────────────

/**
 * Landing page after login.
 * Editors land on hotels (they have no access to reports).
 * Everyone else lands on reports.
 */
export const getDefaultRoute = (role?: string | null): string => {
  if (isEditor(role)) return '/apps/hotel-management/hotels'
  if (isReservation(role)) return '/apps/tours/reservations'
  return '/apps/reports-management/reports'
}

// ─── Role display labels (for UI) ─────────────────────────────────────────────

export const getRoleDisplayLabel = (role?: string | null): string => {
  if (isSuperAdmin(role)) return 'Super Admin'
  if (role === ROLES.ADMIN) return 'Admin'
  if (isEditor(role)) return 'Éditeur'
  if (isReservation(role)) return 'Réservation'
  return role ?? ''
}
